import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip } from '@mui/material';

const truncatedTextSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
};

/**
 * A line of grid cell text that ends in an ellipsis when its column is too
 * narrow, the full text in a tooltip then. The text wraps as the cell lets
 * it: sized grid columns keep body text on one line (see
 * sizedColumnsTableSx), natural ones do not, so it changes nothing there.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The text.
 * @param {string} [props.label] - Always-on tooltip naming the value; the
 *   full text joins it when cut.
 * @param {React.ElementType} [props.component] - Element to render.
 * @param {string} [props.className] - Class of the element.
 * @returns {JSX.Element} the text
 */
export default function GridTruncatedText({
    children,
    label,
    component,
    className
}) {
    const [isTruncated, setIsTruncated] = useState(false);

    // Checked on hover: the column width may have changed since render.
    const handleMouseEnter = (event) => {
        const el = event.currentTarget;
        setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    let title = label || '';
    if (isTruncated) title = label ? `${label}: ${children}` : children;

    return (
        <Tooltip title={title} placement="top-start">
            <Box
                component={component}
                className={className}
                sx={truncatedTextSx}
                onMouseEnter={handleMouseEnter}
            >
                {children}
            </Box>
        </Tooltip>
    );
}

GridTruncatedText.propTypes = {
    children: PropTypes.node,
    label: PropTypes.string,
    component: PropTypes.elementType,
    className: PropTypes.string
};

GridTruncatedText.defaultProps = {
    children: null,
    label: undefined,
    component: 'div',
    className: undefined
};
